import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { GET, POST } from "../../service/CRUDService";
import EntityConstants from "../../configs/EntityConstants";
import InformationIcon from "../../resources/icons/Information-blue-icon.svg";
import CfLoader from "../../common/CfLoader/CfLoader";
import toast from "react-hot-toast";
import { ChevronDown, ChevronUp } from "react-feather";
import VerificationNavbar from "../CombinedValidate/VerificationNavbar";
import ProgressStepper from "../CombinedValidate/ProgressStepper";
import isPdfFile from "../../utils/isPdfFile";
import { useTranslation } from "react-i18next";
import { useBrowserWarningNavigate } from "../../hooks/useBrowserWarningNavigate";
import { TwoPagePdfViewer } from "../../components/TwoPagePdfViewer/TwoPagePdfViewer";
import { observeRazorpaySuccessButton } from "../../utils/observeRazorpaySuccessButton";

const PaymentDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();

  const isVCFlow = location.pathname.includes("/vc/");
  const isLegacyFlow = location.pathname.includes("/legacy/");
  const isOCRFlow = location.pathname.includes("/ocr/");

  const [activeSection, setActiveSection] = useState(
    isOCRFlow ? "vc" : isVCFlow ? "vc" : "legacy",
  );
  const [currentStep, setCurrentStep] = useState(
    isOCRFlow ? 2 : isVCFlow ? 1 : 2,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [apiDataLoading, setApiDataLoading] = useState(false);

  // Data from previous steps
  const [certificateData, setCertificateData] = useState({});
  const [paymentData, setPaymentData] = useState({});
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  // User details
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userContact, setUserContact] = useState("");
  const razorpayObserverCleanupRef = useRef(null);

  useEffect(
    () => () => {
      if (razorpayObserverCleanupRef.current) {
        razorpayObserverCleanupRef.current();
        razorpayObserverCleanupRef.current = null;
      }
    },
    [],
  );

  // Get URL parameters
  const transaction_id = params.transaction_id;
  const order_id = params.order_id; // Only available for VC flow
  const { transaction_verification_log_id } = location.state || {};

  const steps = {
    vc: [t("verify:upload"), t("verify:payment"), t("verify:summary")],
    ocr: [
      t("verify:ocr"),
      t("verify:details"),
      t("verify:payment"),
      t("verify:summary"),
    ],
    legacy: [
      t("verify:select_certificate"),
      t("verify:details"),
      t("verify:payment"),
      t("verify:summary"),
    ],
  };

  const currentSteps = isOCRFlow
    ? steps.ocr
    : isVCFlow
      ? steps.vc
      : steps.legacy;

  const stablePdfUrl = React.useMemo(() => {
    if (!paymentData.uploadedCertificate) return null;
    return paymentData.uploadedCertificate;
  }, [paymentData.uploadedCertificate]);

  const fetchPaymentStatus = async (transactionId, orderId = null) => {
    if (!transactionId) return null;

    try {
      setApiDataLoading(true);

      let payload, endpoint;

      if (isVCFlow) {
        payload = {
          order_id: order_id,
          verification_log_id: transactionId,
        };
        endpoint = EntityConstants.PAYMENT_STATUS;
      } else {
        payload = {
          verification_log_id: transactionId,
        };
        endpoint = EntityConstants.LEGACY_PAYMENT_STATUS;
      }

      const response = await POST(endpoint, payload);

      if (response.ok) {
        const data = await response.json();
        return data;
      } else {
        console.error("Failed to fetch payment status");
        return null;
      }
    } catch (error) {
      console.error("Error in fetching payment status:", error);
      return null;
    } finally {
      setApiDataLoading(false);
    }
  };

  const fetchDataForBackNavigation = async (verificationLogId) => {
    try {
      setIsLoading(true);

      const payload = {
        verification_log_id: verificationLogId,
      };

      const response = await POST(
        EntityConstants.LEGACY_PAYMENT_STATUS,
        payload,
      );

      if (response.ok) {
        const data = await response.json();
        const apiData = data.data;
        const isOthersSelected =
          typeof certificateData.isOthersSelected === "boolean"
            ? certificateData.isOthersSelected
            : apiData.programme_code === "Others" ||
              apiData.batch_code === "Others";

        let formFields = {};
        let components = null;

        if (apiData.verification_form) {
          if (
            apiData.verification_form.verification_fields &&
            apiData.verification_form.verification_fields.components
          ) {
            components =
              apiData.verification_form.verification_fields.components;
          } else if (apiData.verification_form.components) {
            components = apiData.verification_form.components;
          }
        } else {
          toast.error(t("verify:verification_form_not_found"));
        }

        if (components && Array.isArray(components)) {
          components.forEach((component) => {
            formFields[component.key] = {
              label: component.label,
              type: component.inputType === "text" ? "text" : component.type,
              required: component.validate?.required || false,
              placeholder: component.placeholder || "",
              default: apiData.verification_data?.[component.key] || "",
            };
            if (
              component.type === "datetime" ||
              component.key.toLowerCase().includes("date")
            ) {
              formFields[component.key].type = "date";
            }
          });
        } else {
          toast.error(t("verify:verification_form_not_found"));
        }

        const stateData = {
          postVerificationLogId: apiData.verification_log_id || "",
          postIssuerName: apiData.issuer_name,
          postIssuerCode: apiData.org_code || "",
          postProgrammeName: apiData.programme_name,
          postProgrammeDisplayName: apiData.programme_display_name,
          postProgrammeCode: apiData.programme_code || "",
          postBatchName: apiData.batch_name || "",
          postBatchCode: apiData.batch_code || "",
          postTemplateName: apiData.certificate_name,
          postCredentialTemplate: apiData.category_code || "",
          postCertificateName: apiData.certificate_name,
          isOthersSelected,
          customProgrammeName: isOthersSelected
            ? apiData.programme_name ||
              certificateData.customProgrammeName ||
              ""
            : "",
          customBatchName: isOthersSelected
            ? apiData.batch_name || certificateData.customBatchName || ""
            : "",
          formFields: formFields,
          uploadedFile: apiData.uploaded_document
            ? {
                url: apiData.uploaded_document,
                type: apiData.uploaded_document.includes(".pdf")
                  ? "application/pdf"
                  : "image/jpeg",
                name: "uploaded_document",
              }
            : null,
          postComments: apiData.comments || "",
          apiResponse: apiData,
          documentType: apiData.document_type,
          modeOfVerification: apiData.mode_of_verification,
        };
        navigate("/verifier/legacy/user-details", { state: stateData });
      } else {
        const errorText = await response.text();
        toast.error(t("verify:failed_load_prev_data") + errorText);

        // Fallback navigation
        const fallbackStateData = {
          ...certificateData,
          apiResponse: paymentData,
        };
        navigate("/verifier/legacy/user-details", { state: fallbackStateData });
      }
    } catch (error) {
      console.error("Error fetching data for back navigation:", error);
      toast.error(t("verify:failed_load_prev_data_network"));

      // Fallback navigation
      const fallbackStateData = {
        ...certificateData,
        apiResponse: paymentData,
      };
      navigate("/verifier/legacy/user-details", { state: fallbackStateData });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (transaction_verification_log_id) {
      fetchPaymentStatus(transaction_verification_log_id);
    }
  }, [transaction_verification_log_id]);

  // Load data from location state or API
  useEffect(() => {
    const loadData = async () => {
      const stateData = location.state;

      // If we have direct URL access (no state), fetch from API
      if (!stateData && transaction_id) {
        const apiResponse = await fetchPaymentStatus(transaction_id, order_id);

        if (apiResponse) {
          const apiData =
            isVCFlow ? apiResponse : (apiResponse.data || apiResponse);

          if (isVCFlow) {
            setPaymentData({
              paymentRequired: apiData.is_payment_required !== false,
              issuerName: apiData.issuer_name,
              programmeName: apiData.programme_name,
              programmeDisplayName: apiData.programme_display_name,
              razorpayClientId: apiData.razorpay_client_id || "",
              verificationTotalAmount: apiData.total_verification_cost || 0,
              verificationAmount:
                apiData.verification_cost?.verification_fee || 0,
              igstPercentage: apiData.verification_cost?.igst_percentage,
              cgstPercentage: apiData.verification_cost?.cgst_percentage,
              sgstPercentage: apiData.verification_cost?.sgst_percentage,
              gstPercentage: apiData.verification_cost?.gst_percentage,
              platformFee: apiData.verification_cost?.platform_fee || 0,
              igst: apiData.verification_cost?.igst_fee,
              cgst: apiData.verification_cost?.cgst_fee,
              sgst: apiData.verification_cost?.sgst_fee,
              gst: apiData.verification_cost?.gst_fee,
              uploadedCertificate: apiData.uploaded_document || "",
              orderId: apiData.order_id || "",
              verificationLogId: apiData.verification_log_id || transaction_id,
              issuerOrganisation:
                apiData.organization_name || apiData.issuer_name,
              paymentStatus: apiData.payment_status,
              status: apiData.status,
              comments: apiData.comments || "",
              certificateId: apiData.certificate_id || "",
              recipientName: apiData.recipient_name || "",
              recipientEmail: apiData.recipient_email || "",
              documentType: isOCRFlow ? "OCR" : "VC",
            });
          } else {
            setPaymentData({
              paymentRequired: apiData.is_payment_required,
              issuerName: apiData.issuer_name,
              programmeName: apiData.programme_name,
              programmeDisplayName: apiData.programme_display_name,
              batchName: apiData.batch_name,
              templateName: apiData.certificate_name,
              razorpayClientId: apiData.razorpay_client_id,
              verificationTotalAmount: apiData.total_verification_cost,
              verificationAmount:
                apiData.verification_cost?.verification_fee || 0,
              igstPercentage: apiData.verification_cost?.igst_percentage,
              cgstPercentage: apiData.verification_cost?.cgst_percentage,
              sgstPercentage: apiData.verification_cost?.sgst_percentage,
              gstPercentage: apiData.verification_cost?.gst_percentage,
              platformFee: apiData.verification_cost?.platform_fee || 0,
              igst: apiData.verification_cost?.igst_fee,
              cgst: apiData.verification_cost?.cgst_fee,
              sgst: apiData.verification_cost?.sgst_fee,
              gst: apiData.verification_cost?.gst_fee,
              uploadedCertificate: apiData.uploaded_document,
              orderId: apiData.order_id,
              verificationLogId: apiData.verification_log_id || transaction_id,
              issuerOrganisation: apiData.org_name,
              issuerLogo: apiData.issuer_logo,
              paymentStatus: apiData.payment_status,
              status: apiData.status,
              comments: apiData.comments,
              verificationData: apiData.verification_data,
              verificationForm: apiData.verification_form,
              modeOfVerification: apiData.mode_of_verification,
              documentType: apiData.document_type,
            });

            if (apiData.verification_data) {
              setCertificateData({
                formData: apiData.verification_data,
                postIssuerName: apiData.issuer_name,
                postProgrammeName: apiData.programme_name,
                postProgrammeDisplayName: apiData.programme_display_name,
                postBatchName: apiData.batch_name,
              });
            }
          }
        }
      } else if (stateData) {
        if (isOCRFlow && stateData.flowType === "ocr") {
          setPaymentData({
            paymentRequired: stateData.paymentRequired !== false,
            issuerName: stateData.issuerName,
            programmeName: stateData.programmeName,
            razorpayClientId: stateData.razorpayClientId || "",
            verificationTotalAmount: stateData.verificationTotalAmount || 0,
            verificationAmount: stateData.verificationAmount || 0,
            igstPercentage: stateData.igstPercentage,
            cgstPercentage: stateData.cgstPercentage,
            sgstPercentage: stateData.sgstPercentage,
            gstPercentage: stateData.gstPercentage,
            platformFee: stateData.platformFee || 0,
            igst: stateData.igst,
            cgst: stateData.cgst,
            sgst: stateData.sgst,
            gst: stateData.gst,
            uploadedCertificate: stateData.uploadedDocument,
            orderId: stateData.orderId,
            verificationLogId: stateData.verificationLogId,
            issuerOrganisation: stateData.issuerOrganisation,
            certificateId: stateData.certificateId,
            recipientName: stateData.recipientName,
            recipientEmail: stateData.recipientEmail,
            documentType: "OCR",
            status: stateData.status,
            comments: stateData.comments,
            ocrData: stateData.ocrData,
          });

          if (stateData.ocrData) {
            setCertificateData({
              formData: stateData.ocrData,
              postIssuerName: stateData.issuerName,
              postProgrammeName: stateData.programmeName,
            });
          }
        }
        // VC Flow from transaction page
        else if (isVCFlow && stateData.transaction_verification_log_id) {
          const apiResponse = await fetchPaymentStatus(
            stateData.transaction_verification_log_id,
          );

          if (apiResponse) {
            const apiData = apiResponse;

            setPaymentData({
              paymentRequired: apiData.is_payment_required !== false,
              issuerName: apiData.issuer_name,
              programmeName: apiData.programme_name,
              programmeDisplayName: apiData.programme_display_name,
              razorpayClientId: apiData.razorpay_client_id || "",
              verificationTotalAmount: apiData.total_verification_cost || 0,
              verificationAmount:
                apiData.verification_cost?.verification_fee || 0,
              igstPercentage: apiData.verification_cost?.igst_percentage,
              cgstPercentage: apiData.verification_cost?.cgst_percentage,
              sgstPercentage: apiData.verification_cost?.sgst_percentage,
              gstPercentage: apiData.verification_cost?.gst_percentage,
              platformFee: apiData.verification_cost?.platform_fee || 0,
              igst: apiData.verification_cost?.igst_fee,
              cgst: apiData.verification_cost?.cgst_fee,
              sgst: apiData.verification_cost?.sgst_fee,
              gst: apiData.verification_cost?.gst_fee,
              uploadedCertificate: apiData.uploaded_document || "",
              orderId: apiData.order_id || "",
              verificationLogId:
                apiData.verification_log_id ||
                stateData.transaction_verification_log_id,
              issuerOrganisation:
                apiData.organization_name || apiData.issuer_name,
              paymentStatus: apiData.payment_status,
              status: apiData.status,
              comments: apiData.comments || "",
              certificateId: apiData.certificate_id || "",
              recipientName: apiData.recipient_name || "",
              recipientEmail: apiData.recipient_email || "",
              documentType: "VC",
            });
          } else {
            toast.error(t("verify:failed_load_transaction_details"));
            navigate("/verifier/validate");
          }
        }
        // VC Flow from Validate.jsx (normal flow)
        else if (isVCFlow && !stateData.transaction_verification_log_id) {
          setPaymentData({
            paymentRequired: stateData.paymentRequired !== false,
            issuerName: stateData.issuerName,
            programmeName: stateData.programmeName,
            razorpayClientId: stateData.razorpayClientId,
            verificationTotalAmount: stateData.verificationTotalAmount,
            verificationAmount: stateData.verificationAmount || 0,
            igstPercentage: stateData.igstPercentage,
            cgstPercentage: stateData.cgstPercentage,
            sgstPercentage: stateData.sgstPercentage,
            gstPercentage: stateData.gstPercentage,
            platformFee: stateData.platformFee || 0,
            igst: stateData.igst,
            cgst: stateData.cgst,
            sgst: stateData.sgst,
            gst: stateData.gst,
            uploadedCertificate: stateData.uploadedDocument,
            orderId: stateData.orderId,
            verificationLogId: stateData.verificationLogId,
            issuerOrganisation: stateData.issuerOrganisation,
            certificateId: stateData.certificateId,
            recipientName: stateData.underReviewCandidateName,
            recipientEmail: stateData.underReviewCandidateEmail,
            documentType: stateData.document_type,
            status: stateData.underReviewStatus,
            comments: stateData.reviewcomments,
          });
        }
        // Legacy Flow and OCR Flow from transaction
        else if (isLegacyFlow || (isOCRFlow && stateData.transaction_verification_log_id)) {
          if (stateData.apiResponse) {
            const apiData = stateData.apiResponse;
            setPaymentData({
              paymentRequired: apiData.is_payment_required,
              issuerName: apiData.issuer_name,
              programmeName: apiData.programme_name,
              programmeDisplayName: apiData.programme_display_name,
              batchName: apiData.batch_name,
              templateName: apiData.certificate_name,
              razorpayClientId: apiData.razorpay_client_id,
              verificationTotalAmount: apiData.total_verification_cost,
              verificationAmount:
                apiData.verification_cost?.verification_fee || 0,
              igstPercentage: apiData.verification_cost?.igst_percentage,
              cgstPercentage: apiData.verification_cost?.cgst_percentage,
              sgstPercentage: apiData.verification_cost?.sgst_percentage,
              gstPercentage: apiData.verification_cost?.gst_percentage,
              platformFee: apiData.verification_cost?.platform_fee || 0,
              igst: apiData.verification_cost?.igst_fee,
              cgst: apiData.verification_cost?.cgst_fee,
              sgst: apiData.verification_cost?.sgst_fee,
              gst: apiData.verification_cost?.gst_fee,
              uploadedCertificate: apiData.uploaded_document,
              orderId: apiData.order_id,
              verificationLogId: apiData.verification_log_id,
              issuerOrganisation: apiData.issuer_organisation,
            });

            // Set certificate data for legacy
            setCertificateData({
              postIssuerName: stateData.postIssuerName,
              postIssuerCode: stateData.postIssuerCode,
              postProgrammeName: stateData.postProgrammeName,
              postProgrammeCode: stateData.postProgrammeCode,
              postBatchName: stateData.postBatchName,
              postBatchCode: stateData.postBatchCode,
              postCredentialTemplate: stateData.postCredentialTemplate,
              postCertificateName: stateData.postCertificateName,
              isOthersSelected: stateData.isOthersSelected,
              customProgrammeName: stateData.customProgrammeName,
              customBatchName: stateData.customBatchName,
              formData: stateData.formData,
              uploadedFile: stateData.uploadedFile,
              postComments: stateData.postComments,
            });
          } else if (stateData.transaction_verification_log_id) {
            const apiResponse = await fetchPaymentStatus(
              stateData.transaction_verification_log_id,
            );

            if (apiResponse) {
              const apiData = apiResponse.data;
              setPaymentData({
                paymentRequired: apiData.is_payment_required,
                issuerName: apiData.issuer_name,
                programmeName: apiData.programme_name,
                programmeDisplayName: apiData.programme_display_name,
                batchName: apiData.batch_name,
                templateName: apiData.certificate_name,
                razorpayClientId: apiData.razorpay_client_id,
                verificationTotalAmount: apiData.total_verification_cost,
                verificationAmount:
                  apiData.verification_cost?.verification_fee || 0,
                igstPercentage: apiData.verification_cost?.igst_percentage,
                cgstPercentage: apiData.verification_cost?.cgst_percentage,
                sgstPercentage: apiData.verification_cost?.sgst_percentage,
                gstPercentage: apiData.verification_cost?.gst_percentage,
                platformFee: apiData.verification_cost?.platform_fee || 0,
                igst: apiData.verification_cost?.igst_fee,
                cgst: apiData.verification_cost?.cgst_fee,
                sgst: apiData.verification_cost?.sgst_fee,
                gst: apiData.verification_cost?.gst_fee,
                uploadedCertificate: apiData.uploaded_document,
                orderId: apiData.order_id,
                verificationLogId: apiData.verification_log_id,
                issuerOrganisation: apiData.org_name,
                issuerLogo: apiData.issuer_logo,
                paymentStatus: apiData.payment_status,
                status: apiData.status,
                comments: apiData.comments,
                verificationData: apiData.verification_data,
                verificationForm: apiData.verification_form,
                modeOfVerification: apiData.mode_of_verification,
                documentType: apiData.document_type,
              });
            }
          }
        }
      } else {
        if (isOCRFlow) {
          navigate("/verifier/validate");
        } else if (isVCFlow) {
          navigate("/verifier/validate");
        } else {
          navigate("/verifier/legacy/user-details");
        }
      }
    };

    loadData();
  }, [
    location.state,
    navigate,
    transaction_id,
    order_id,
    isVCFlow,
    isOCRFlow,
    isLegacyFlow,
  ]);

  const handleSectionChange = (section) => {
    setActiveSection(section);
    if (section === "vc") {
      navigate("/verifier/validate");
    } else if (section === "legacy") {
      navigate("/verifier/validate", {
        state: { showLegacySection: "legacy" },
      });
    }
  };

  const getUserDetails = async () => {
    try {
      const response = await GET(EntityConstants.CURRENT_USER);
      if (response.ok) {
        const data = await response.json();
        setUserName(data.first_name + " " + data.last_name);
        setUserEmail(data.email);
        setUserContact(data.phone);
      }
    } catch (error) {
      console.error("Failed to fetch user details", error);
    }
  };

  const handleRazorpayPayment = async () => {
    const primaryColor =
      getComputedStyle(document.documentElement).getPropertyValue(
        "--cf-primary-color",
      ) || "#3538CD";

    setIsLoading(true);

    const cleanupRazorpayObserver = () => {
      if (razorpayObserverCleanupRef.current) {
        razorpayObserverCleanupRef.current();
        razorpayObserverCleanupRef.current = null;
      }
    };

    const options = {
      key: paymentData.razorpayClientId,
      amount: paymentData.verificationTotalAmount,
      currency: "INR",
      name: paymentData.issuerName,
      description: paymentData.programmeName,
      order_id: paymentData.orderId,
      handler: (response) => {
        cleanupRazorpayObserver();
        const nextStepData = {
          ...certificateData,
          ...paymentData,
          paymentId: response.razorpay_payment_id,
          paymentSignature: response.razorpay_signature,
          currentStep: isOCRFlow ? 3 : isVCFlow ? 2 : 3,
          flowType: isOCRFlow ? "ocr" : isVCFlow ? "vc" : "legacy",
        };

        localStorage.setItem("payment_id", response.razorpay_payment_id);
        setIsLoading(false);

        if (isOCRFlow) {
          navigate("/verifier/ocr/summary", { state: nextStepData });
        } else if (isVCFlow) {
          navigate("/verifier/vc/summary", { state: nextStepData });
        } else {
          navigate("/verifier/legacy/summary", { state: nextStepData });
        }
      },
      prefill: {
        name: userName,
        email: userEmail,
        contact: userContact,
      },
      theme: {
        color: primaryColor,
      },
      modal: {
        ondismiss: () => {
          cleanupRazorpayObserver();
          setIsLoading(false);
        },
      },
    };

    const rzp = new window.Razorpay(options);
    cleanupRazorpayObserver();
    razorpayObserverCleanupRef.current = observeRazorpaySuccessButton();
    rzp.open();
    rzp.on("payment.failed", (response) => {
      cleanupRazorpayObserver();
      setIsLoading(false);
      toast.error(t("verify:payment_failed_retry"));
      console.error("Payment failed:", response);
    });
  };

  const handleBackClick = () => {
    if (isOCRFlow) {
      navigate("/verifier/ocr/form", {
        state: {
          fromPayment: true,
          transactionId: paymentData.verificationLogId,
          s3FileUrl: paymentData.uploadedCertificate,
          flowType: "ocr",
          currentStep: 1,
        },
      });
    } else if (isVCFlow) {
      navigate("/verifier/validate");
    } else {
      if (paymentData.verificationLogId) {
        fetchDataForBackNavigation(paymentData.verificationLogId);
      } else {
        const stateData = {
          ...certificateData,
          apiResponse: paymentData,
        };
        navigate("/verifier/legacy/user-details", { state: stateData });
      }
    }
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  useBrowserWarningNavigate(t("verify:unsaved_changes_warning"));

  const actualAmount = (paymentData.verificationTotalAmount || 0) / 100;
  const actualVerificationAmount = (paymentData.verificationAmount || 0) / 100;
  const actualPlatformFee = (paymentData.platformFee || 0) / 100;
  const actualGst = (paymentData.gst || 0) / 100;
  const actualSgst = (paymentData.sgst || 0) / 100;
  const actualCgst = (paymentData.cgst || 0) / 100;
  const actualIgst = (paymentData.igst || 0) / 100;

  if (isLoading || apiDataLoading) {
    return (
      <div className="w-full min-h-screen bg-cfSecondary pt-20 pb-10 flex items-center justify-center">
        <CfLoader />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-[6rem] bg-white px-4">
      <VerificationNavbar
        activeSection={activeSection}
        handleSectionChange={handleSectionChange}
      />
      <ProgressStepper steps={currentSteps} currentStep={currentStep} />

      <div className="space-y-4">
        <div className="mb-[5rem] lg:mb-[0rem]">
          <div className="flex flex-col lg:flex-row mt-4 w-full">
            {paymentData.uploadedCertificate && (
              <div className="w-full lg:w-[50%] gap-5">
                <div className="w-full lg:w-[100%] border border-gray-300 rounded-lg mb-3 lg:mb-0 flex flex-col justify-between order-2 lg:order-1">
                  <div className="w-full h-[400px] bg-gray-100 text-center border border-gray-300 rounded-lg overflow-hidden flex items-center justify-center">
                    {isPdfFile(
                      paymentData.uploadedCertificate,
                      paymentData.documentType,
                    ) ? (
                      <TwoPagePdfViewer
                        pdfUrl={stablePdfUrl}
                        showZoom={false}
                        heightClassName="h-full"
                        viewerClassName="border-none bg-none"
                      />
                    ) : (
                      <img
                        src={paymentData.uploadedCertificate}
                        alt="Certificate Preview"
                        className={`object-contain w-full h-full ${
                          imageLoading ? "hidden" : "block"
                        }`}
                        onLoad={() => setImageLoading(false)}
                        onError={() => setImageLoading(false)}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}
            {paymentData.uploadedCertificate && (
              <div className="w-[2px] bg-gray-300 mx-5"></div>
            )}
            <div
              className={`${
                paymentData.uploadedCertificate ? "w-full lg:w-[50%]" : "w-full"
              }`}
            >
              <div className="bg-[#EAEBFF] rounded p-4 flex items-start gap-2">
                <img src={InformationIcon} className="mt-[0.2rem]" alt="" />
                <p className="text-gray-900">
                  {t("verify:verification_intro_issued_by")}{" "}
                  <span className="font-semibold">
                    {paymentData.issuerName}
                  </span>{" "}
                  {paymentData.programmeDisplayName && (
                    <>
                      {t("verify:under_the")}{" "}
                      <span className="font-semibold">
                        {paymentData.programmeDisplayName}
                      </span>{" "}
                      {t("verify:programme")}
                    </>
                  )}
                  {paymentData.batchName && (
                    <>
                      {t("verify:for_the")}{" "}
                      <span className="font-semibold">
                        {paymentData.batchName}
                      </span>{" "}
                      {t("verify:batch")}
                    </>
                  )}
                  {paymentData.templateName && (
                    <>
                      {t("verify:using_the")}{" "}
                      <span className="font-semibold">
                        {paymentData.templateName}
                      </span>{" "}
                      {t("verify:template")}
                    </>
                  )}
                </p>
              </div>
              <div className="w-full py-3 text-start text-2xl font-semibold text-cfPrimary">
                <div className="flex gap-2">
                  <div>{t("verify:verification_fee")}</div>
                  <div>₹{actualAmount}</div>
                </div>
              </div>
              <div className="mb-8">
                {/* Payment Summary Accordion */}
                <div className="border rounded-lg overflow-hidden">
                  <button
                    id="payment-toggle-summary"
                    onClick={() => setIsDetailsOpen(!isDetailsOpen)}
                    className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors focus:outline-none"
                  >
                    <span className="font-medium text-gray-900">
                      {t("verify:payment_summary")}
                    </span>
                    {isDetailsOpen ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {isDetailsOpen && (
                    <div className="px-4 py-3 bg-white border-t">
                      <div className="space-y-2">
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">
                            {t("verify:verification_amount")}
                          </span>
                          <span className="font-medium">
                            ₹{actualVerificationAmount}
                          </span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-600">
                            {t("verify:platform_fee")}
                          </span>
                          <span className="font-medium">
                            ₹{actualPlatformFee}
                          </span>
                        </div>
                        {paymentData.igstPercentage && actualIgst && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">
                              {t("verify:igst")} ({paymentData.igstPercentage})
                            </span>
                            <span className="font-medium">₹{actualIgst}</span>
                          </div>
                        )}
                        {paymentData.cgstPercentage && actualCgst && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">
                              {t("verify:cgst")} ({paymentData.cgstPercentage})
                            </span>
                            <span className="font-medium">₹{actualCgst}</span>
                          </div>
                        )}
                        {paymentData.sgstPercentage && actualSgst && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">
                              {t("verify:sgst")} ({paymentData.sgstPercentage})
                            </span>
                            <span className="font-medium">₹{actualSgst}</span>
                          </div>
                        )}
                        {paymentData.gstPercentage && actualGst && (
                          <div className="flex justify-between items-center text-sm">
                            <span className="text-gray-600">
                              {t("verify:gst")} ({paymentData.gstPercentage})
                            </span>
                            <span className="font-medium">₹{actualGst}</span>
                          </div>
                        )}
                        <div className="pt-2 mt-2 border-t flex justify-between items-center font-medium">
                          <span>{t("verify:total_amount")}</span>
                          <span>₹{actualAmount}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <p className="mb-4 text-center text-gray-900 font-semibold mt-[1rem]">
                {t("verify:proceed_payment_instruction")}
              </p>
              <div
                className={`flex items-center ${
                  isOCRFlow || isVCFlow || isLegacyFlow
                    ? "justify-between"
                    : "justify-center "
                }`}
              >
                {(isOCRFlow || isVCFlow || isLegacyFlow) && (
                  <button
                    id="payment-back-button"
                    onClick={handleBackClick}
                    className="cursor-pointer py-2.5 px-[3.40rem] text-sm font-medium text-gray-700 bg-white hover:!bg-gray-50 focus:outline-none rounded-[8px] border border-gray-300 focus:z-10 focus:ring-3 focus:ring-gray-100"
                  >
                    {isOCRFlow || isLegacyFlow
                      ? t("verify:back")
                      : t("verify:cancel")}
                  </button>
                )}
                <button
                  id="payment-pay-now-button"
                  onClick={handleRazorpayPayment}
                  className="cursor-pointer py-2.5 px-[3.40rem] text-sm font-medium text-white bg-cfButton hover:bg-cfPrimaryDark focus:outline-none rounded-[8px] border border-gray-200 focus:z-10 focus:ring-3 focus:ring-gray-100"
                >
                  {t("verify:pay_now")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetails;
